import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { admin } from '../../api/client.js';
import { useApi } from '../../hooks/useApi.js';
import { useTenant } from '../../context/TenantContext.js';
import { Badge } from '../../components/Badge.js';
import { Modal } from '../../components/Modal.js';
import { LoadingSpinner } from '../../components/LoadingSpinner.js';
import { ErrorMessage } from '../../components/ErrorMessage.js';
import type { AdminTenant, AdminUser, PlatformHealth } from '../../types/index.js';

function CreateTenantModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [guildId, setGuildId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { data: userData, loading: usersLoading } = useApi<{ users: AdminUser[] }>(
    () => admin.users(),
    [],
  );
  const userList = userData?.users ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !guildId || !ownerId) return;
    setSaving(true);
    setError('');
    try {
      await admin.createServerForUser({ name, guildId, ownerId });
      setName('');
      setGuildId('');
      setOwnerId('');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create server');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Server">
      <form onSubmit={(e) => void handleSubmit(e)} className="modal-form">
        <div className="form-group">
          <label className="form-label">Server Name</label>
          <input
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. My Discord Server"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Discord Guild ID</label>
          <input
            className="form-input"
            value={guildId}
            onChange={(e) => setGuildId(e.target.value)}
            placeholder="e.g. 123456789012345678"
            style={{ fontFamily: 'monospace' }}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Owner</label>
          {usersLoading ? (
            <LoadingSpinner text="Loading users..." />
          ) : (
            <select
              className="form-input"
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
            >
              <option value="">Select an owner...</option>
              {userList.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.discordUsername} ({u.discordId})
                </option>
              ))}
            </select>
          )}
        </div>

        {error && <div className="form-error">{error}</div>}

        <button
          type="submit"
          className="btn-sm btn-primary"
          style={{ width: '100%', padding: 12, fontSize: 14 }}
          disabled={saving || !name || !guildId || !ownerId}
        >
          {saving ? 'Creating...' : 'Create Server'}
        </button>
      </form>
    </Modal>
  );
}

export function AdminTenantsPage() {
  const navigate = useNavigate();
  const { selectTenant } = useTenant();
  const [modalOpen, setModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const { data, loading, error, refetch } = useApi<{ tenants: AdminTenant[] }>(
    () => admin.tenants(),
    [],
  );
  const { data: healthData } = useApi<PlatformHealth>(
    () => admin.health(),
    [],
  );

  const tenantList = data?.tenants ?? [];
  const health = healthData?.platform;

  const handleSuspend = async (tenantId: string) => {
    setActionLoading(tenantId);
    try {
      await admin.suspendServer(tenantId);
      void refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to suspend');
    } finally {
      setActionLoading('');
    }
  };

  const handleActivate = async (tenantId: string) => {
    setActionLoading(tenantId);
    try {
      await admin.activateServer(tenantId);
      void refetch();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to activate');
    } finally {
      setActionLoading('');
    }
  };

  if (loading) return <LoadingSpinner text="Loading servers..." />;
  if (error) return <ErrorMessage message={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>All Servers</h2>
          <p style={{ fontSize: 13, color: 'var(--t3)' }}>Every registered server on the platform</p>
        </div>
        <button className="btn-sm btn-primary" onClick={() => setModalOpen(true)}>+ New Server</button>
      </div>

      {/* platform health summary */}
      {health && (
        <div className="stats-row" style={{ marginBottom: 20 }}>
          <div className="stat-card">
            <div className="stat-label">Total Servers</div>
            <div className="stat-value">{health.totalServers}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Active Bots</div>
            <div className="stat-value" style={{ color: 'var(--green)' }}>{health.activeBots}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Stuck Sessions</div>
            <div className="stat-value" style={{ color: health.stuckSessions > 0 ? 'var(--red)' : 'var(--green)' }}>{health.stuckSessions}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Completed (24h)</div>
            <div className="stat-value" style={{ color: 'var(--green)' }}>{health.completed24h}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Expired Roles</div>
            <div className="stat-value" style={{ color: health.expiredActiveRoles > 0 ? 'var(--amber)' : 'var(--t3)' }}>{health.expiredActiveRoles}</div>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Guild ID</th>
              <th>Owner</th>
              <th>Bot</th>
              <th>Config</th>
              <th>Issues</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tenantList.map((t) => {
              const missingWallet = !t.walletSolana;
              const missingLog = !t.logChannelId;
              const hasIssues = t.stuckPayments > 0 || t.unsweptBalance > 0;
              return (
                <tr key={t.id} style={hasIssues ? { background: 'rgba(231,76,60,0.04)' } : undefined}>
                  <td style={{ color: 'var(--t1)', fontWeight: 500 }}>{t.name}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--t3)' }}>{t.guildId}</td>
                  <td style={{ color: 'var(--t2)' }}>{t.ownerUsername ?? t.ownerDiscordId ?? '—'}</td>
                  <td>
                    <Badge
                      status={t.botActive ? 'active' : 'expired'}
                      label={t.botActive ? 'Running' : 'Stopped'}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {missingWallet && (
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(243,156,18,0.15)', color: 'var(--amber)' }}>
                          No wallet
                        </span>
                      )}
                      {missingLog && (
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(149,165,166,0.15)', color: 'var(--t3)' }}>
                          No log ch
                        </span>
                      )}
                      {!missingWallet && !missingLog && (
                        <span style={{ fontSize: 10, color: 'var(--green)' }}>OK</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexDirection: 'column', fontSize: 11 }}>
                      {t.stuckPayments > 0 && (
                        <span style={{ color: 'var(--red)', fontWeight: 600 }}>
                          {t.stuckPayments} stuck
                        </span>
                      )}
                      {t.unsweptBalance > 0 && (
                        <span style={{ color: 'var(--amber)' }}>
                          {t.unsweptBalance.toFixed(4)} unswept
                        </span>
                      )}
                      {t.stuckPayments === 0 && t.unsweptBalance === 0 && (
                        <span style={{ color: 'var(--t3)' }}>—</span>
                      )}
                    </div>
                  </td>
                  <td style={{ color: 'var(--t3)', fontSize: 12 }}>
                    {new Date(t.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="btn-sm btn-ghost"
                        style={{ fontSize: 11, padding: '3px 8px', color: 'var(--violet)', fontWeight: 600 }}
                        onClick={() => {
                          void selectTenant(t.id).then((ok) => {
                            if (ok) navigate('/');
                          });
                        }}
                      >
                        Manage
                      </button>
                      {t.botActive ? (
                        <button
                          className="btn-sm btn-ghost"
                          style={{ fontSize: 11, padding: '3px 8px', color: 'var(--red)' }}
                          disabled={actionLoading === t.id}
                          onClick={() => void handleSuspend(t.id)}
                        >
                          {actionLoading === t.id ? '...' : 'Suspend'}
                        </button>
                      ) : (
                        <button
                          className="btn-sm btn-ghost"
                          style={{ fontSize: 11, padding: '3px 8px', color: 'var(--green)' }}
                          disabled={actionLoading === t.id}
                          onClick={() => void handleActivate(t.id)}
                        >
                          {actionLoading === t.id ? '...' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateTenantModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => void refetch()}
      />
    </>
  );
}
